'use client'

import React from 'react'
import Link from 'next/link'
import type { IActualite } from '@/types/actualite'

// ---------------------------------------------------------------------------
// ReadMoreArrow — shared icon
// ---------------------------------------------------------------------------

const ReadMoreArrow: React.FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M15 8C15 4.13401 11.866 1 8 1C4.13401 1 1 4.13401 1 8C1 11.866 4.13401 15 8 15C11.866 15 15 11.866 15 8ZM4.75 7.25C4.33579 7.25 4 7.58579 4 8C4 8.41421 4.33579 8.75 4.75 8.75L9.43934 8.75L8.21967 9.96967C7.92678 10.2626 7.92678 10.7374 8.21967 11.0303C8.51256 11.3232 8.98744 11.3232 9.28033 11.0303L11.7803 8.53033C12.0732 8.23744 12.0732 7.76256 11.7803 7.46967L9.28033 4.96967C8.98744 4.67678 8.51256 4.67678 8.21967 4.96967C7.92678 5.26256 7.92678 5.73744 8.21967 6.03033L9.43934 7.25L4.75 7.25Z"
      fill="#1B365E"
    />
  </svg>
)

// ---------------------------------------------------------------------------
// NewsCard — presentational
// When `href` is provided the "Lire l'article" CTA becomes a Next.js Link.
// ---------------------------------------------------------------------------

interface NewsCardProps {
  item: IActualite
  href?: string
}

export const NewsCard: React.FC<NewsCardProps> = ({ item, href }) => {
  const cta = href ? (
    <Link
      href={href}
      className="flex items-center gap-2 font-bold text-[#1B365E] text-[15px] leading-4.5 transition-colors hover:text-[#2c5282] mt-auto"
    >
      <ReadMoreArrow />
      Lire l&apos;article
    </Link>
  ) : (
    <button className="flex items-center gap-2 font-bold text-[#1B365E] text-[15px] leading-4.5 cursor-pointer transition-colors hover:text-[#2c5282] mt-auto">
      <ReadMoreArrow />
      Lire l&apos;article
    </button>
  )

  return (
    <div className="bg-[#FFE4B4] pt-6 md:pt-7 md:pr-6 pr-[14.46px] md:pl-8.25 pl-[27.22px] pb-5.75 rounded-2xl h-full flex flex-col">
      <h3 className="text-[#1B365E] font-semibold text-[16px] leading-5.75 font-fustat">
        {item.title}
      </h3>

      <p className="text-[#1D1D1D] text-[8px] tracking-[3%] leading-2.75 my-4.25 font-fustat">
        {item.published_at ?? '—'}
      </p>

      <p className="text-[#1B365E] text-sm leading-5 mb-10 font-fustat flex-1">
        {item.description ?? item.excerpt}
      </p>

      {cta}
    </div>
  )
}

// ---------------------------------------------------------------------------
// NewsCardSkeleton
// ---------------------------------------------------------------------------

export const NewsCardSkeleton: React.FC = () => (
  <div className="bg-[#FFE4B4]/40 rounded-2xl h-64 animate-pulse" />
)
